import { AddressFragment } from "@src/lib/contexts/LocationDetailContext/LocationDetailContext.generated";
import { Input, InputProps } from "@src/lib/ui/Input";
import { useKeystrokes, KeyStrokeConfig } from "@src/lib/hooks/useKeystrokes";
import { cn } from "@src/lib/utils/cn";
import { AddressInput } from "@src/types.generated";
import { FC, useEffect, useRef, useState } from "react";

type ExtendedAddressInput = AddressInput & {
  timezone?: string;
};

type Props = {
  onAddressChange: (address: ExtendedAddressInput) => void;
  initialAddress?: ExtendedAddressInput;
  keyStrokeConfig?: KeyStrokeConfig;
  inputLabel?: string;
  textFieldProps?: Partial<InputProps>;
  hasValidationError?: boolean;
};

const AddressField: FC<Props> = ({
  onAddressChange,
  initialAddress,
  keyStrokeConfig,
  inputLabel,
  textFieldProps,
  hasValidationError,
}) => {
  const [query, setQuery] = useState(
    initialAddress ? addressInputToString(initialAddress) : "",
  );
  useEffect(() => {
    if (initialAddress) {
      setQuery(addressInputToString(initialAddress));
    }
  }, [initialAddress]);
  const autoCompleteRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!autoCompleteRef.current) {
      return;
    }
    const autoComplete = new window.google.maps.places.Autocomplete(
      autoCompleteRef.current,
      {
        types: ["address"],
      },
    );
    autoComplete.setFields([
      "address_components",
      "formatted_address",
      "geometry",
    ]);
    autoComplete.addListener("place_changed", () => {
      const addressObject = autoComplete.getPlace();
      setQuery(addressObject.formatted_address || "");
      const addressInput = googlePlaceToAddress(addressObject);
      if (addressInput) {
        const { lat, lng } = addressInput;
        if (lat && lng) {
          const timestamp = Math.floor(Date.now() / 1000);
          fetch(
            `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`,
          )
            .then((response) => response.json())
            .then((data) => {
              if (data.status === "OK") {
                onAddressChange({
                  ...addressInput,
                  timezone: data.timeZoneId,
                });
              } else {
                onAddressChange(addressInput);
              }
            })
            .catch(() => {
              onAddressChange(addressInput);
            });
        } else {
          onAddressChange(addressInput);
        }
      }
    });
  }, [onAddressChange]);

  useKeystrokes(keyStrokeConfig);
  return (
    <Input
      label={inputLabel}
      ref={autoCompleteRef}
      onChange={(e) => setQuery(e.target.value)}
      placeholder="Search address..."
      value={query}
      {...textFieldProps}
      inputClassName={cn(
        textFieldProps?.inputClassName,
        hasValidationError && "border-red-9 focus-visible:ring-red-9",
      )}
    />
  );
};

export default AddressField;

const googlePlaceToAddress = (
  place: google.maps.places.PlaceResult,
): AddressInput | null => {
  let lineOne = "";
  let lineTwo = "";
  let city = "";
  let state = "";
  let country = "";
  let postalCode = "";
  if (!place.address_components) {
    return null;
  }
  for (const component of place.address_components) {
    const componentType = component.types[0];
    switch (componentType) {
      case "street_number": {
        lineOne = `${component.long_name} ${lineOne}`;
        break;
      }

      case "route": {
        lineOne += component.short_name;
        break;
      }

      case "subpremise":
        lineTwo = component.long_name;
        break;

      case "postal_code": {
        postalCode = `${component.long_name}${postalCode}`;
        break;
      }

      case "postal_code_suffix": {
        postalCode = `${postalCode}-${component.long_name}`;
        break;
      }

      case "locality":
        city = component.long_name;
        break;

      case "sublocality_level_1":
        city = component.long_name;
        break;

      case "administrative_area_level_1": {
        state = component.short_name;
        break;
      }

      case "country":
        country = component.short_name;
        break;
    }
  }

  const lat = place.geometry?.location?.lat();
  const lng = place.geometry?.location?.lng();

  return {
    lineOne,
    lineTwo,
    city,
    state,
    country,
    postalCode,
    lat,
    lng,
  };
};

export const addressFragmentToString = (address: AddressFragment): string => {
  const parts: string[] = [];

  const streetAddress =
    `${address.lineOne || ""}${address.lineTwo ? ` ${address.lineTwo}` : ""}`.trim();
  if (streetAddress) parts.push(streetAddress);
  if (address.city) parts.push(address.city);
  if (address.usState) parts.push(address.usState);
  if (address.postalCode) parts.push(address.postalCode);
  if (address.country) parts.push(address.country);

  return parts.join(", ");
};

export const addressInputToString = (address: AddressInput): string => {
  const parts: string[] = [];

  const streetAddress =
    `${address.lineOne || ""}${address.lineTwo ? ` ${address.lineTwo}` : ""}`.trim();
  if (streetAddress) parts.push(streetAddress);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.postalCode) parts.push(address.postalCode);
  if (address.country) parts.push(address.country);

  return parts.join(", ");
};
